<script setup>
    import { ref, computed } from 'vue';
    import * as yup from 'yup';
    import { useForm } from 'vee-validate';
    import axios from 'axios';
    import utils from '@/utils.js';

    const { handleSubmit } = useForm({
        validationSchema: yup.object({
            name: yup.string().nullable().required().label("Account Name"),
            number: yup.string().nullable().required().label("Account Number"),
            inventoryBranch: yup.string().nullable().required("Required").label("Inventory Branch"),
            salesBranch: yup.string().nullable().required("Required").label("Sales Branch"),
            hasGroundService: yup.boolean()
                .required("Required")
                .oneOf([true], 'At least one "Ground" shipping service is required.'),
        })
    });

    const model = ref({});
    const root = ref(null);

    const carriers = ref([]);
    const selectedCarriers = ref([]);
    const serviceTypes = ref([]);
    const selectedServiceTypes = ref([]);

    let onSave = null;

    function switchFiller() {
        selectedCarriers.value = [];
    }

    const onSubmit = handleSubmit(async () => {
        try {
            onSave(model.value);
        }
        finally {
            toggleDisplay(false)
        }
    });

    function updateSelectedCarriers(event) {
        toggleCarrier(event.target.value);
    }

    function toggleCarrier(carrier) {
        var index = selectedCarriers.value.indexOf(carrier);

        if (model.value.areVesselsFilledByLinde == false) selectedCarriers.value = [];

        if (index < 0) {
            selectedCarriers.value.push(carrier);
        }
        else {
            selectedCarriers.value.splice(index, 1);
        }

        //force update of ground options - R.S asked to remove on 2/28/2025

        /*var groundServices = serviceTypes.value.filter(x => x.isGround == true);
        groundServices.forEach(x => {
            if (!hasCarrier(x.carrier) && hasServiceType(x)) toggleServiceType(x);
            if (hasCarrier(x.carrier) && !hasServiceType(x)) toggleServiceType(x);
        });*/
    }

    function toggleServiceType(item) {
        var index = selectedServiceTypes.value.indexOf(item);

        if (index >= 0)
            selectedServiceTypes.value.splice(index, 1);
        else
            selectedServiceTypes.value.push(item);
    }

    function isServiceTypeDisabled(item) {
        if (!hasCarrier(item.carrier)) return true;
        return item.isGround;
    }

    async function open(item) {
        selectedCarriers.value = [];
        const response = await axios.get(`/api/Customer/Account/${(item?.id || "")}`);            
        model.value = (response.status === 200) ? response.data : null;

        //load the service types
        const serviceTypeResponse = await axios.get('/api/Order/ShippingServiceTypes');
        serviceTypes.value = serviceTypeResponse.data;

        //set the carriers array based on the available service types
        carriers.value = serviceTypes.value.map(x => x.carrier).filter((x, i, a) => a.indexOf(x) == i);

        var activeServiceTypes = serviceTypes.value.filter(x => model.value.serviceTypeIds.indexOf(x.id) >= 0);
        activeServiceTypes.forEach(x => toggleServiceType(x));

        var activeCarriers = activeServiceTypes.map(x => x.carrier).filter((x, i, a) => a.indexOf(x) == i);
        activeCarriers.forEach(x => toggleCarrier(x));


        return new Promise((resolve, reject) => {
            //assign callbacks
            onSave = (result) => {
                const dto = Object.assign(item || {}, result);                
                dto.serviceTypeIds = selectedServiceTypes.value.filter(x => hasCarrier(x.carrier)).map(x => x.id);
                resolve(dto)
            };
            toggleDisplay(true);
        });
    };

    const toggleDisplay = (value) => root.value.style.display = value ? "block" : "none";

    async function getRandomCustomerNumber() {
        const response = await axios.post("/api/Customer/CreateRandomAccountNumber");
        if (response.status !== 200) return;
        model.value.number = response.data;
    }

    const hasCarrier = (carrier) => selectedCarriers.value.indexOf(carrier) >= 0;
    const hasServiceType = (serviceType) => selectedServiceTypes.value.indexOf(serviceType) >= 0;
    const hasGroundService = computed(() => selectedServiceTypes.value.filter(x => x.isGround == true ).length > 0);
    const applicableServices = computed(() => serviceTypes.value.filter(x => hasCarrier(x.carrier)));

    defineExpose({
        open
    })
</script>

<template>
    <div class="modal" ref="root">
        <form @submit.prevent="onSubmit">
            <div class="modal-background"></div>
            <div class="modal-card mt-6" v-if="model != null">

                <header class="modal-card-head">
                    <p class="modal-card-title">{{model && model.id > 0 ? "Edit" : "Add"}} Customer Account</p>
                    <button type="button" class="delete" aria-label="close" v-on:click="toggleDisplay(false)" title="close"></button>
                </header>
                <section class="modal-card-body">
                    <div class="field">
                        <label class="label">Account Name</label>
                        <div class="control">
                            <Field class="input" type="text" name="name" v-model="model.name" />
                            <ErrorMessage class="errors" name="name" />
                        </div>
                    </div>

                    <div class="field">
                        <label class="label">Account Number</label>

                        <div class="columns">
                            <div class="column">
                                <div class="control">
                                    <Field class="input" type="text" name="number" v-model="model['number']" />
                                    <ErrorMessage class="errors" name="number" />
                                </div>
                            </div>
                            <div class="column is-narrow">
                                <button type="button"
                                        v-bind:disabled="(model.number || '').length > 0"
                                        class="button is-info is-light" @click="getRandomCustomerNumber()">
                                    <i class="material-icons md-18 mr-2">casino</i> Generate
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="field is-grouped">
                        <div class="control">
                            <label class="label">Linde Inventory Branch *</label>
                            <div class="control">
                                <Field class="input" name="inventoryBranch" v-model="model.inventoryBranch"></Field>
                                <ErrorMessage class="errors" name="inventoryBranch" />
                            </div>
                        </div>
                        <div class="control">
                            <label class="label">Linde Sales Branch *</label>
                            <div class="control">
                                <Field class="input" name="salesBranch" v-model="model.salesBranch"></Field>
                                <ErrorMessage class="errors" name="salesBranch" />
                            </div>
                        </div>
                    </div>

                    <div class="field">
                        <p class="label">Who is normally responsible for filling the shipping vessels?</p>

                        <div class="radios">
                            <label class="radio">
                                <input type="radio" name="areVesselsFilledByLinde" v-model="model.areVesselsFilledByLinde" :value="true" @click="switchFiller" /> Linde Fills - select shipping services from 1 or more carriers.
                            </label>
                        </div>
                        <div class="control">
                            <label class="radio">
                                <input type="radio" name="areVesselsFilledByLinde" v-model="model.areVesselsFilledByLinde" :value="false" @click="switchFiller" /> Customer Fills - select shipping services from only 1 carrier.
                            </label>
                        </div>
                    </div>

                    <div class="field">
                        <label class="label">Carriers Supported for Orders</label>
                        <div class="control">
                            <label class="checkbox mr-1" v-for="item in carriers">
                                <input v-bind:type="( model.areVesselsFilledByLinde == false ? 'radio' : 'checkbox')"
                                       :value="item"
                                       name="carrier"
                                       @click="updateSelectedCarriers"
                                       v-bind:checked="hasCarrier(item)" />
                                <span class="ml-1" v-html="utils.formatShippingCarrier(item)"></span>
                            </label>
                        </div>
                    </div>

                    <div class="field">
                        <label class="label">Applicable Services</label>
                        
                        <Field as="hidden" name="hasGroundService" v-model="hasGroundService"></Field>
                        <ErrorMessage class="errors" name="hasGroundService" />

                        <div v-if="applicableServices.length > 0">
                            <table class="table is-narrow">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Carrier</th>
                                        <th>Shipping Service</th>
                                        <th>Lead Time (days)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in applicableServices">
                                        <td>
                                            <i class="material-icons md-18 cursor-help mr-2" :title="item.description">help</i>

                                            <input type="checkbox"
                                                   :value="item.id"
                                                   @click="toggleServiceType(item)"
                                                   v-bind:checked="hasServiceType(item)" />
                                        </td>
                                        <td><span v-html="utils.formatShippingCarrier(item.carrier)"></span></td>
                                        <td>
                                            {{item.name}}
                                        </td>
                                        <td>{{item.leadTimeInDays}} days</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else class="is-italic">
                            Please select a carrier in order to see the available services.
                        </div>
                    </div>
                </section>
                <footer class="modal-card-foot is-justify-content-right">
                    <div class="buttons">
                        <button type="submit" class="button is-success">OK</button>
                        <button type="button" class="button" @click="toggleDisplay(false)">Cancel</button>
                    </div>
                </footer>
            </div>
        </form>
    </div>
</template>

